import React, { BaseSyntheticEvent, FC, useState } from "react";
import sweets from "./sweets.png";
import copy from "./icons/copy-icon.svg";
import { useTranslation } from "react-i18next";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { useNavigate } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { isPublishShow } from "../../../graphql/state";
import { usePageQuery } from "../../../graphql/page.hooks";
import {
  Check2,
  ChevronLeft,
  ChevronRight,
  Compass,
  QrCodeScan,
  Share,
  WindowStack,
} from "react-bootstrap-icons";
import { SHARE_SOCIALS } from "./constans";

type Props = {
  show: boolean;
  onCancel?: () => void;
};

export const PublishModal: FC<Props> = ({ show, onCancel }) => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const publishModal = useReactiveVar(isPublishShow);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [openShare, setOpenShare] = useState<boolean>(publishModal.sharePage);

  const { data: page } = usePageQuery({
    skip: !publishModal.pageId,
    variables: { id: publishModal.pageId },
  });

  const copyLink = async () => {
    try {
      setIsCopied(true);
      await navigator.clipboard.writeText(page?.page.publicUrl);

      setTimeout(() => {
        setIsCopied(false);
      }, 1000);
    } catch (error) {
      console.error(error);
    }
  };

  const openPage = () => {
    if (page?.page) window.location.href = page?.page.publicUrl;
  };

  const openMyProjects = () => {
    navigate(`${lng}/`);
    onCancel();
  };

  const openCreateQr = () => {
    navigate(`${lng}/entry/${publishModal.pageId}`);
    onCancel();
  };

  const openSharePage = () => {
    setOpenShare(true);
  };

  const closeSharePage = () => {
    setOpenShare(false);
  };

  const closeByBackground = (e: BaseSyntheticEvent) => {
    e.stopPropagation();
    if (e.target.id === "confirmModal") onCancel();
  };

  const PAGE_PURPOSE_TYPE = page?.page?.purposeType || 1;

  const ACTIONS_LIST = [
    {
      title: t("modal.openPage"),
      icon: Compass,
      type: [2, 3],
      callback: () => openPage(),
    },
    {
      title: t("share.sharePage"),
      icon: Share,
      type: [1, 3],
      callback: () => openSharePage(),
    },
    {
      title: t("qr.createQR"),
      icon: QrCodeScan,
      type: [1, 2],
      callback: () => openCreateQr(),
    },
    {
      title: t("menu.myProjects"),
      icon: WindowStack,
      type: [1, 2, 3],
      callback: () => openMyProjects(),
    },
  ];

  const PAGE_PURPOSES = [
    {
      text: t(
        "modal.Your page is published! Share it and start making an impact."
      ),
      btnIcon: <Compass className="text-white me-2" />,
      btnText: t("modal.openPage"),
      callback: () => openPage(),
    },
    {
      text: t(
        "modal.Share it with your network and let people discover your links instantly."
      ),
      btnIcon: <Share className="text-white me-2" />,
      btnText: t("share.sharePage"),
      callback: () => openSharePage(),
    },
    {
      text: t(
        "modal.Use your digital business card anywhere — just share it instantly with a QR code."
      ),
      btnIcon: <QrCodeScan className="text-white me-2" />,
      btnText: t("modal.Create Page QR"),
      callback: () => openCreateQr(),
    },
  ];

  return (
    <div
      id="confirmModal"
      className={`modal ${
        show ? "show d-flex" : ""
      } align-items-center justify-content-center`}
      style={{
        display: show ? "block" : "none",
        background: "rgb(0, 0, 0, 0.6)",
      }}
      onClick={(e) => closeByBackground(e)}
    >
      <div className="publishModal">
        <div className="modal-content text-break d-flex flex-column align-items-center rounded-4">
          <div className="col-12 modal-header pb-2 border-0">
            {openShare && !publishModal.sharePage && (
              <ChevronLeft
                onClick={closeSharePage}
                className="fs-4 text-dark cursor-pointer"
              />
            )}
            <button
              className="btn-close"
              aria-label="Close"
              onClick={onCancel}
              style={{ height: "16px", width: "16px", opacity: "1" }}
            ></button>
          </div>
          {!openShare ? (
            <div
              className={`d-flex flex-column-reverse ${page?.page?.type === 1 ? "align-items-md-end" : "align-items-md-center"} align-items-center flex-md-row col-12 pb-3 pb-md-4 px-3 px-md-4 overflow-hidden`}
            >
              <div className="col-12 col-md-6 d-flex flex-column align-items-center mt-2 mt-md-0">
                <img
                  src={sweets}
                  className="mb-4 d-none d-md-block"
                  alt="sweets picture"
                />
                <h4
                  className="fs-3 text-dark fw-bold mb-2 mb-md-1 text-center"
                  style={{ fontSize: "24px" }}
                >
                  {t("modal.Page published!")}
                </h4>
                <p className="col-9 fs-md-6 text-gray mb-3 mb-md-4 text-center">
                  {PAGE_PURPOSES[PAGE_PURPOSE_TYPE - 1].text}
                </p>
                <div className="col-12 d-flex align-items-center justify-content-between px-4 mb-3 mb-md-4">
                  {ACTIONS_LIST.filter(({ type }) =>
                    type.includes(PAGE_PURPOSE_TYPE)
                  ).map(({ title, icon, callback }, i) => {
                    const Icon = icon;

                    return (
                      <div
                        key={i}
                        className="d-flex flex-column align-items-center"
                      >
                        <div
                          className="d-flex align-items-center justify-content-center bg-primary-light rounded-3 mb-2 cursor-pointer"
                          style={{ height: "52px", width: "52px" }}
                          onClick={callback}
                        >
                          <Icon className="text-primary fs-5" />
                        </div>
                        <p className="text-dark fs-7 m-0">{title}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="col-12 p-3 bg-primary-light rounded-3 d-flex aloign-items-center justify-content-between mb-3">
                  <p className="col-10 text-primary fs-6 mb-0 text-nowrap overflow-hidden overflow-ellipsis">
                    {page?.page?.publicUrl.slice(
                      8,
                      page?.page?.publicUrl.length
                    )}
                  </p>

                  {!isCopied ? (
                    <img
                      className="cursor-pointer"
                      src={copy}
                      onClick={copyLink}
                      alt="copy icon"
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : (
                    <Check2 className="text-primary fs-5" />
                  )}
                </div>
                <button
                  onClick={PAGE_PURPOSES[PAGE_PURPOSE_TYPE - 1].callback}
                  className="btn btn-primary text-white col-12 rounded-3 fw-bold fs-6 py-3"
                  style={{ height: "56px" }}
                >
                  {PAGE_PURPOSES[PAGE_PURPOSE_TYPE - 1].btnIcon}
                  {PAGE_PURPOSES[PAGE_PURPOSE_TYPE - 1].btnText}
                </button>
              </div>
              <div
                className={`pagePreview ${page?.page?.type === 1 ? "pc" : "mobile"} col-12 col-md-6 d-flex justify-content-center ${page?.page?.type === 1 && "mx-md-3"} mb-4 mb-md-0`}
              >
                <div
                  className="col-12"
                  style={{ backgroundImage: `url(${page?.page?.image})` }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="col-12 px-3 px-md-4">
              <h4 className="fs-4 fw-bold text-dark text-center mb-1">
                {t("share.sharePage")}
              </h4>
              <p className="text-primary mb-4 fs-6 text-center">
                {page.page?.publicUrl.slice(8, page.page?.publicUrl.length)}
              </p>
              <ul className="socials col-12 d-flex flex-wrap p-0">
                {SHARE_SOCIALS.map(({ icon, text, shareLink }, i) => (
                  <li key={i} className="col-12 col-md-6 list-unstyled">
                    <a
                      href={`${shareLink}${page?.page?.publicUrl}`}
                      className="d-flex text-decoration-none align-items-center justify-content-between p-3"
                    >
                      <div className="d-flex">
                        <img
                          className="fs-5"
                          src={icon}
                          alt="icon"
                          style={{ marginRight: "12px" }}
                        />
                        <p className="fs-6 text-dark m-0">
                          {t(`modal.${text}`)}
                        </p>
                      </div>
                      <ChevronRight className="text-dark fs-5" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
